import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Loader from "../../components/loader";
import { LayoutForNoting } from "../../components/layout";
import { siteMode } from "../../cores/siteMode";

import Loadable from "@cores/loadable";
import menuService from "@services/menuService";
import Stage from "@routes/stage/Stage";

import { setStage } from "@modules/stage";
import { setProject } from "@modules/project";
import { getStagesProjects, getUser } from "@modules/user";
import { getProjectSettings, getStageSettings } from "@modules/settings";
import { applyDefaultLanguage } from "@lib/i18n";

import { sessionKeeper } from "../../cores/sessionKeeper";
import InvalidUser from "@routes/errors/InvalidUser";

const LoadableTokenExpiredModal = Loadable({
    loader: () => import("../../components/modals/TokenExpired"),
    loading: () => null,
});

const User = ({ children, ...props }) => {
    const { t, i18n } = useTranslation();

    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { spaceId, projectId } = menuService.parse(location.pathname);

    const user = useSelector(({ user }) => user);
    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const token = useSelector(({ token }) => token);
    const projectGeneralSettings = useSelector(({ settings }) => settings?.project?.General);

    // 프로젝트 설정을 한 번이라도 받아온 프로젝트인지 — 아래 보정 effect 가 최초 조회와 중복되지 않게 한다.
    const fetchedProjectIdRef = useRef(null);

    const [noSiteAccess, setNoSiteAccess] = useState({ key: null, value: false, errorType: null });

    const NO_SITE_ACCESS_KEY = {
        NO_SITE_ACCESS_1: "NO_SITE_ACCESS_1",
        NO_SITE_ACCESS_2: "NO_SITE_ACCESS_2",
        NO_SITE_ACCESS_3: "NO_SITE_ACCESS_3",
        NO_SITE_ACCESS_4: "NO_SITE_ACCESS_4",
        NO_PERMISSION_TO_VIEW_PAGE_1: "NO_PERMISSION_TO_VIEW_PAGE_1",
        NO_PERMISSION_TO_VIEW_PAGE_2: "NO_PERMISSION_TO_VIEW_PAGE_2",
    };
    const closeNoAccessPopup = () => {
        history.replace("/spaces");
    };

    const isAdminSetting = useMemo(() => {
        if (!location.pathname) return false;

        return location.pathname.indexOf(`/admin/spaces/${spaceId}`) !== -1 && !projectId;
    }, [location]);

    /**
     * @param {object} stage
     * @param {boolean} [shouldApplySpaceDefaultLanguage] 이 스페이스 설정의 기본 언어를 적용할지 여부.
     *   프로젝트 설정이 스페이스 값을 상속·오버라이드해 내려주므로 평소에는 프로젝트 쪽에서 적용하고,
     *   프로젝트를 설정하지 않는 호출부(스페이스 설정 화면으로 바로 진입)에서만 true 로 넘긴다.
     *   양쪽에서 적용하면 두 응답의 도착 순서에 따라 프로젝트 오버라이드가 스페이스 값에 덮인다.
     */
    const updateSettings = async (stage, shouldApplySpaceDefaultLanguage = false) => {
        const settings = await dispatch(
            getStageSettings({
                stageId: stage.id,
                apiEndpoint: stage.endpoint,
            }),
        );
        if (shouldApplySpaceDefaultLanguage)
            await applyDefaultLanguage(settings?.General?.consoleDefaultLanguage?.value);
    };

    const updateProjectSettings = async (stage, project, resource) => {
        const settings = await dispatch(
            getProjectSettings({
                stageId: stage.id,
                apiEndpoint: stage.endpoint,
                projectId: project.id,
                params: {
                    resource,
                },
            }),
        );

        if (settings && resource === "General") {
            await applyDefaultLanguage(settings?.General?.consoleDefaultLanguage?.value);
        }
    };

    useEffect(() => {
        if (!token?.value) return;
        if (!user.data) {
            dispatch(getUser());
        }
        if (!user.stages.data) {
            dispatch(getStagesProjects());
        }
    }, [token.value]);

    //stage, project 설정
    useEffect(() => {
        const locationPathname = window.location.pathname;
        if (["/library/spaces", "/spaces", "/access-token-bridge", "/snsLogin/youtube"].indexOf(locationPathname) !== -1) return; //space목록에서는 space권한체크 불필요함
        if (!user.data || !user.stages?.data || user.stages.allProjectsPending) return;

        let spaceKey = spaceId;
        let projectKey = projectId;
        if (user.stages.data.length === 1 && user.stages.data[0]?.projects?.length === 1) {
            dispatch(setStage(user.stages.data[0]));
            dispatch(setProject(user.stages.data[0].projects[0]));
            updateSettings(user.stages.data[0]);
        } else if (user.stages.data.length >= 1) {
            if (location.pathname === `/` || location.pathname === "/library/") {
                const currentSession = sessionKeeper.get();
                if (currentSession) {
                    if (
                        !currentSession.stageId ||
                        !currentSession.projectId ||
                        (user &&
                            user.stages &&
                            user.stages.data &&
                            user.stages.data.length > 0 &&
                            user.stages.data.filter((stg) => stg.id === currentSession.stageId).length === 0)
                    ) {
                        history.replace(`${location.pathname}spaces`);
                        return;
                    }

                    spaceKey = currentSession.stageId;
                    projectKey = currentSession.projectId;
                }
            }

            if ((!isAdminSetting && !(spaceKey && projectKey)) || (isAdminSetting && !spaceKey)) {
                setNoSiteAccess({ key: NO_SITE_ACCESS_KEY.NO_SITE_ACCESS_1, value: true, errorType: "NO_SITE_ACCESS" });
                return;
            }

            const foundSpace = user.stages.data.find((stage) => stage.id === spaceKey);
            if (!foundSpace) {
                setNoSiteAccess({ key: NO_SITE_ACCESS_KEY.NO_SITE_ACCESS_2, value: true, errorType: "NO_SITE_ACCESS" });
                return;
            }

            dispatch(setStage(foundSpace));
            // 스페이스 설정 화면은 프로젝트를 설정하지 않아 프로젝트 설정이 로드되지 않는다 → 스페이스 기본 언어를 적용
            updateSettings(foundSpace, isAdminSetting);

            if (!isAdminSetting) {
                const foundProject = foundSpace?.projects?.find((project) => project.id === projectKey);
                if (!foundProject) {
                    setNoSiteAccess({
                        key: NO_SITE_ACCESS_KEY.NO_SITE_ACCESS_3,
                        value: true,
                        errorType: "NO_SITE_ACCESS",
                    });
                    return;
                }
                dispatch(setProject(foundProject));
            }
        } else {
            const welcomePath = window.location.pathname.indexOf("/library") > -1 ? "/library/welcome" : "/welcome";
            history.replace(welcomePath);
        }
    }, [user.stages.allProjectsPending]);

    useEffect(() => {
        if (!stage?.id || !project?.id) return;

        const fetchProjectSettings = async () => {
            try {
                const response = await Promise.allSettled([
                    updateProjectSettings(stage, project, "MenuName"),
                    updateProjectSettings(stage, project, "General"),
                ]);
                const rejectedResults = response.filter((result) => result.status === "rejected");
                if (rejectedResults.length > 0) {
                    throw new Error("update project settings failed");
                }
            } catch (e) {
                console.log(e);
            } finally {
                fetchedProjectIdRef.current = project.id;
            }
        };

        fetchProjectSettings();
    }, [project?.id]);

    /**
     * 프로젝트 설정이 한 번 들어왔다가 비워지면 다시 받아온다.
     *
     * 관리자 화면들이 이탈·진입 시 settings 를 reset 하는데, settings 는 헤더 테마·메뉴 노출 등
     * 앱 전역이 읽는 상태다. 위 재조회는 프로젝트가 바뀔 때만 돌아서, 같은 프로젝트로 돌아오면
     * 새로고침 전까지 빈 채로 남는다. [DAM-821]
     *
     * 최초 조회와 중복 요청하지 않도록 이미 한 번 받아온 프로젝트인지 ref 로 가른다.
     * 조회가 실패하면 의존성이 그대로라 다시 돌지 않고, 404 면 빈 객체가 채워져 가드에 걸려 멈춘다.
     */
    useEffect(() => {
        if (!stage?.id || !project?.id || projectGeneralSettings) return;
        if (fetchedProjectIdRef.current !== project.id) return;

        updateProjectSettings(stage, project, "General").catch(console.error);
        // stage/project 객체와 updateProjectSettings 는 매 렌더 새로 만들어져 의존성에 넣으면 매번 돈다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage?.id, project?.id, projectGeneralSettings]);

    useEffect(() => {
        if (!stage || !stage.version || !stage.user) return;
        const locationPathname = window.location.pathname;

        if (
            locationPathname !== "/spaces" &&
            locationPathname !== "/library/spaces" &&
            locationPathname !== "/" &&
            locationPathname !== "/library/" &&
            !isAdminSetting
        ) {
            const userData = stage.user;
            let hasNoSiteAccess = true;
            if (userData && userData.projects) {
                const userProject = userData.projects.filter((p) => p.id === projectId)[0];
                if (stage.id === spaceId && userProject) {
                    const siteAccess = userProject.siteAccess;
                    if (siteAccess) {
                        const mode = siteMode.get();

                        // NOTE: 라이브러리 권한만 있는 사용자가 콘솔로 진입 시 라이브러리로 redirect
                        if (mode === "CONSOLE" && !siteAccess.includes("CONSOLE") && siteAccess.includes("CMS")) {
                            if (location.pathname.indexOf("/library") === -1) {
                                const fullPath = `/library${location.pathname}${location.search}`;
                                window.location.href = `${window.location.origin}${fullPath}`;
                                return;
                            }
                        }

                        hasNoSiteAccess =
                            (mode === "CONSOLE" && siteAccess.indexOf("CONSOLE") === -1) ||
                            (mode === "CMS" && siteAccess.indexOf("CMS") === -1);
                    }
                }
                if (hasNoSiteAccess) {
                    setNoSiteAccess({
                        key: NO_SITE_ACCESS_KEY.NO_SITE_ACCESS_4,
                        value: true,
                        errorType: "NO_SITE_ACCESS",
                    });
                }
            }
        }

        if (location.pathname.indexOf(`/admin/spaces/${spaceId}`) !== -1 && !projectId) {
            const userData = stage.user;
            if (userData && !userData.isSpaceAdmin) {
                setNoSiteAccess({
                    key: NO_SITE_ACCESS_KEY.NO_PERMISSION_TO_VIEW_PAGE_1,
                    value: true,
                    errorType: "NO_PERMISSION_TO_VIEW_PAGE",
                });
            }
        } else if (location.pathname.indexOf(`/admin/spaces/${spaceId}/projects/${projectId}`) === 0) {
            const userData = stage.user;
            if (userData && userData.projects) {
                const isAdmin =
                    userData.isSpaceAdmin ||
                    userData.projects.filter((p) => p.id === project.id && p.isAdmin).length > 0;
                if (!isAdmin) {
                    setNoSiteAccess({
                        key: NO_SITE_ACCESS_KEY.NO_PERMISSION_TO_VIEW_PAGE_2,
                        value: true,
                        errorType: "NO_PERMISSION_TO_VIEW_PAGE",
                    });
                }
            }
        }
    }, [stage.user]);

    if (noSiteAccess.value) {
        return <LoadableTokenExpiredModal error={{ type: noSiteAccess.errorType }} onConfirm={closeNoAccessPopup} />;
    }
    if (
        location.pathname.indexOf("/access-token-bridge") !== -1 ||
        location.pathname.indexOf("/snsLogin/youtube") !== -1
    ) {
        return <>{children}</>;
    }
    if (user.pending || user.stages.stagePending || user.stages.allProjectsPending) {
        return <Loader />;
    } else if (user.data != null && user.stages.data !== null) {
        if ((!isAdminSetting && stage.id !== "" && project.id !== "") || (isAdminSetting && stage.id !== "")) {
            return <>{children}</>;
        } else {
            return <Stage />;
        }
    } else if (user.error || user.stages.error) {
        return <InvalidUser />;
    } else {
        return null;
    }
};

export default User;
